import {Button, Modal} from 'antd'
import {curriculum_getByOwner_Api} from 'apis'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {CurriculumActionTypes} from 'views/AppLocals/Course'
import {
    CreateCurriculumModal,
    EditCurriculumModal,
    EditSubjectParamModal,
    LinkSubjectModal,
} from 'views/AppLocals/Course/Curriculum/CurriculumForm'
import {useCurriculum} from 'views/AppLocals/Course/Curriculum/useCurriculum'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import {AchievedCertBox, renderCurriculumCardItem, renderCurriculumTableRow} from '../functions/renderCurriculum'
import {BiLink, BiSolidEdit, BiTrash, BiUnlink} from 'react-icons/bi'
import {RiSearchEyeLine} from 'react-icons/ri'
import {GoDotFill} from 'react-icons/go'
import {BsBook, BsGrid3X3GapFill, BsInfoCircle, BsTable} from 'react-icons/bs'
import {PiMedalFill} from 'react-icons/pi'
import getTitle from 'helpers/getTitle'
import LoadingBlock from "../../../Loading/LoadingBlock";
import {TbEdit} from "react-icons/tb";
import classNames from "classnames";
import PagePaginationWrapper from "../../../../modules/asyncCache/components/PagePagination/PagePaginationWrapper";
import EmptyHolder from "../../../../components/EmptyHolder";
import {renderLoading} from "../../../AppLocals/functions/renderers/renderLoading";
import {Section} from "../../../../components/form/ModernForm";

// ─── Subject list inside a curriculum detail ──────────────────────────────────

const CurriculumSubjectRow = ({item, onUnlink, onEditParam}) => {
    const t = useTranslate()
    const {title, number_of_sessions, is_required, passed_score} = item ?? {}
    const is_mandatory = is_required
    return (
        <div className="py-2 px-3 flex items-start justify-between gap-3 hover:background-50">
            <div className="flex flex-col gap-1 flex-1">
                <span className="font-semibold text-sm text-color-000">{title}</span>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {renderIf(
                        number_of_sessions > 0,
                        <span className="text-xs text-color-400 italic tracking-wider">
                            <GoDotFill size={10} className="mr-1 text-color-500"/>
                            {`${t('number sessions')}: ${number_of_sessions}`}
                        </span>
                    )}
                    {renderIf(
                        passed_score !== undefined && passed_score !== null,
                        <span className="text-xs text-color-400 italic tracking-wider">
                            <GoDotFill size={10} className="mr-1 text-color-500"/>
                            {`${t('passed score')}: ${passed_score}`}
                        </span>
                    )}
                    <span className={`text-xs italic ${is_mandatory ? 'text-red-500' : 'text-color-400'}`}>
                        * {is_mandatory ? t('mandatory') : t('optional')}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <div
                    onClick={() => onEditParam(item)}
                    className="w-6 h-6 flex flex-center hover:bg-blue-100 rounded-md cursor-pointer">
                    <TbEdit size={14} className="text-blue-500"/>
                </div>
                <div
                    onClick={() => onUnlink(item)}
                    className="w-6 h-6 flex flex-center hover:bg-green-50 rounded-md cursor-pointer">
                    <BiUnlink size={14} className="text-green-500"/>
                </div>
            </div>
        </div>
    )
}

const CurriculumDetailContent = ({item, onUnlink, onEditParam, onLinkSubject}) => {
    const t = useTranslate()
    const {description, default_medal} = item ?? {}
    const months = item?.duration_in_months ?? item?.duration_months ?? 0
    const subjects = item?.subjects || []

    return (
        <div className="flex flex-col gap-3">
            {/* Chứng nhận - đưa lên trên cùng */}
            <Section icon={PiMedalFill} title={t('achieved certificate')}>
                <AchievedCertBox medal={default_medal} t={t}/>
            </Section>

            <Section icon={BsInfoCircle} title={t('general information')}>
                <div className="font-bold text-sm text-color-000 tracking-wide">
                    {getTitle(item)}
                </div>
                {renderIf(
                    description,
                    <p className="text-color-300 tracking-wide leading-relaxed">
                        {description}
                    </p>
                )}
                {renderIf(
                    months > 0,
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-color-400 italic">{`${t('duration')}:`}</span>
                        <span className="px-2 py-0.5 border border-blue-200 bg-blue-50 rounded-full text-xs font-medium text-blue-500 whitespace-no-wrap">
                            {`${months} ${t('months')}`}
                        </span>
                    </div>
                )}
            </Section>

            <Section icon={BsBook} title={t('subjects')} >
                <div className="flex justify-end">
                    <Button
                        size="small" color="primary" variant="filled"
                        icon={<BiLink size={14}/>}
                        onClick={() => onLinkSubject(item)}
                        className="text-xs rounded-md">
                        {t('link subject')}
                    </Button>
                </div>
                {subjects.length ? (
                    <div className="flex flex-col divide-y divide-color-100 border border-color-50 rounded-xl overflow-hidden background">
                        {subjects.map((s, i) => (
                            <CurriculumSubjectRow
                                key={s.id || i}
                                item={s}
                                onUnlink={onUnlink}
                                onEditParam={onEditParam}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="py-3 text-sm text-color-400 italic">
                        {t('no subjects linked')}
                    </div>
                )}
            </Section>
        </div>
    )
}

// ─── Main container ───────────────────────────────────────────────────────────

const CurriculaManager = ({owner_id, owner_type,                        filter_actions = {
    sortBy: 'sort-by',
    keyword: 'keyword',
},
                          }) => {
    const t = useTranslate()
    const location = useLocation()

    const [action, setAction] = useState({type: undefined, value: undefined})
    const onCancel = () => setAction({})

    // Bảng mặc định (theo yêu cầu), chuyển sang Thẻ qua toggle - chỉ local state, không vào URL.
    // Khớp pattern viewMode ở Subjects.js / Students.js cùng thư mục.
    const [viewMode, setViewMode] = useState('table')

    const {
        refreshKey,
        onCreate = NullAsDefault,
        onUpdate = NullAsDefault,
        onDelete = NullAsDefault,
        onLinkSubject = NullAsDefault,
        onUnlinkSubject = NullAsDefault,
        onUpdateSubjectParam = NullAsDefault,
    } = useCurriculum({
        onCreated: onCancel,
        onUpdated: onCancel,
        onDeleted: onCancel,
        onLinked: onCancel,
        onUnlinked: onCancel,
        onSubjectParamUpdated: onCancel,
    })

    const confirmDelete = (item) => {
        const id = getId(item) || item?.idname
        if (id) {
            showDeleteConfirmDialog({
                translate: t,
                title: 'delete curriculum',
                message: 'are you sure you want to delete this curriculum',
                onOk: () => onDelete({}, {id}),
            })
        }
    }

    const confirmUnlink = (item) => {
        const id = item?.id
        if (id) {
            showDeleteConfirmDialog({
                translate: t,
                title: 'unlink subject',
                message: 'are you sure you want to unlink this subject from curriculum',
                onOk: () => onUnlinkSubject({}, {id}),
            })
        }
    }

    const handleAction = (type, value) => {
        switch (type) {
            case CurriculumActionTypes.CURRICULUM_READ:
            case CurriculumActionTypes.CURRICULUM_CREATE:
            case CurriculumActionTypes.CURRICULUM_UPDATE:
            case CurriculumActionTypes.CURRICULUM_LINK_SUBJECT:
            case CurriculumActionTypes.CURRICULUM_UNLINK_SUBJECT:
                setAction({type, value})
                break
            case CurriculumActionTypes.CURRICULUM_DELETE:
                confirmDelete(value)
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <RiSearchEyeLine size={14} className="text-green-600"/>,
                        label: 'view detail',
                        key: CurriculumActionTypes.CURRICULUM_READ,
                    },
                    {
                        icon: <BiLink size={14} className="text-blue-500"/>,
                        label: 'link subject',
                        key: CurriculumActionTypes.CURRICULUM_LINK_SUBJECT,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: CurriculumActionTypes.CURRICULUM_UPDATE,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: CurriculumActionTypes.CURRICULUM_DELETE,
                    },
                ],
                (key) => handleAction(key, item)
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    const modal = useMemo(() => {
        const id = action?.value?.idname || getId(action?.value)

        let title
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({initialValues: {}})
        let modal_params = {}

        switch (action?.type) {
            case CurriculumActionTypes.CURRICULUM_READ:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('curriculum detail')}
                    </div>
                )
                modal_params = {width: 640, footer: null}
                Content = ({initialValues}) => (
                    <CurriculumDetailContent
                        item={initialValues || action?.value}
                        onUnlink={confirmUnlink}
                        onEditParam={(subjectItem) =>
                            setAction({
                                type: CurriculumActionTypes.CURRICULUM_UNLINK_SUBJECT,
                                value: subjectItem,
                            })
                        }
                        onLinkSubject={(curr) =>
                            setAction({
                                type: CurriculumActionTypes.CURRICULUM_LINK_SUBJECT,
                                value: curr,
                            })
                        }
                    />
                )
                Wrapper = ({children}) =>
                    children({initialValues: action?.value, isLoading: false})
                break

            case CurriculumActionTypes.CURRICULUM_CREATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('create curriculum')}
                    </div>
                )
                modal_params = {width: 600}
                Content = () => (
                    <CreateCurriculumModal
                        owner_id={owner_id}
                        owner_type={owner_type}
                        onCancel={onCancel}
                        onSubmit={(values) =>
                            onCreate(values, {type: owner_type, id: owner_id})
                        }
                    />
                )
                Wrapper = ({children}) => children({initialValues: {}, isLoading: false})
                break

            case CurriculumActionTypes.CURRICULUM_UPDATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit curriculum')}
                    </div>
                )
                modal_params = {width: 600}
                Content = ({initialValues}) => (
                    <EditCurriculumModal
                        owner_id={owner_id}
                        owner_type={owner_type}
                        initialValues={initialValues}
                        onCancel={onCancel}
                        onSubmit={(values) => onUpdate(values, {id})}
                    />
                )
                Wrapper = ({children}) =>
                    children({initialValues: action?.value, isLoading: false})
                break

            case CurriculumActionTypes.CURRICULUM_LINK_SUBJECT:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('link subject to curriculum')}
                    </div>
                )
                modal_params = {width: 560}
                Content = () => (
                    <LinkSubjectModal
                        curriculum_id={id}
                        owner_id={owner_id}
                        owner_type={owner_type}
                        onCancel={onCancel}
                        onSubmit={(params, {curriculum_id, subject_id}) =>
                            onLinkSubject(params, {curriculum_id, subject_id})
                        }
                    />
                )
                Wrapper = ({children}) => children({initialValues: {}, isLoading: false})
                break

            case CurriculumActionTypes.CURRICULUM_UNLINK_SUBJECT:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit subject parameters')}
                    </div>
                )
                modal_params = {width: 480}
                Content = ({initialValues}) => (
                    <EditSubjectParamModal
                        initialValues={initialValues}
                        onCancel={onCancel}
                        onSubmit={(values) =>
                            onUpdateSubjectParam(values, {id: action?.value?.id})
                        }
                    />
                )
                Wrapper = ({children}) =>
                    children({initialValues: action?.value, isLoading: false})
                break

            default:
                break
        }

        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                open={!!action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({isLoading, initialValues}) => {
                        if (isLoading) return <LoadingBlock/>
                        return <Content initialValues={initialValues}/>
                    }}
                </Wrapper>
            </Modal>
        )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, owner_id, owner_type])

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, location.state]
    )

    // Cột: CHƯƠNG TRÌNH HỌC (kèm thời lượng + SỐ MÔN dưới tên) | SỐ KHÓA | CHỨNG NHẬN ĐẠT ĐƯỢC |
    // THAO TÁC - khớp với renderCurriculumTableRow (renderCurriculum.js). Đã bỏ hẳn cột "Môn học
    // thành phần" (liệt kê từng môn) - bấm vào dòng (icon mắt/THAO TÁC) đã sang trang chi tiết thấy
    // đủ danh sách + có sẵn phần thêm/bớt môn ở đó rồi, không cần lặp lại ở bảng danh sách.
    const TABLE_COLUMNS = [
        {label: t('curriculum'), className: 'text-left'},
        {label: t('course count short'), className: 'text-left'},
        {label: t('achieved certificate'), className: 'text-left'},
        {label: t('actions'), className: 'text-right'},
    ]

    const renderList = useCallback(
        ({keyword, sortBy: currentSort, filterBy, refreshKey}) => (
            <PagePaginationWrapper
                apiInfo={curriculum_getByOwner_Api}
                query={bindQueryParams([{type: owner_type}, {id: owner_id}])}
                values={{keyword, sort_by: currentSort, ...(filterBy ?? {})}}
                refreshKey={refreshKey}
                renderItem={
                    viewMode === 'table'
                        ? renderCurriculumTableRow({translate: t, renderContextMenu, handleAction})
                        : renderCurriculumCardItem({translate: t, renderContextMenu, handleAction})
                }
                renderLoading={() => renderLoading()}
                renderEmpty={() => <EmptyHolder/>}
                Wrapper={({children}) => viewMode === 'table' ? (
                    <div className="mt-2 w-full overflow-x-auto rounded-xl border border-color-50">
                        <table className="w-full" style={{borderCollapse: 'collapse'}}>
                            <thead>
                                <tr className="background-200">
                                    {TABLE_COLUMNS.map((col, i) => (
                                        <th key={i} className={classNames("px-3 py-2 text-2xs font-bold text-color-400 uppercase tracking-wider whitespace-no-wrap", col.className)}>
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>{children}</tbody>
                        </table>
                    </div>
                ) : (
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {children}
                    </div>
                )}
            />
        ),
        [owner_id, owner_type, t, renderContextMenu, handleAction, viewMode]
    )

    const viewModeToggle = (
        <div className="flex items-center flex-shrink-0 rounded-lg border border-color-200 overflow-hidden">
            <button
                type="button"
                title={t('table')}
                onClick={() => setViewMode('table')}
                style={{width: 40, height: 40}}
                className={classNames(
                    "flex flex-center transition-colors",
                    viewMode === 'table' ? 'bg-secondary-500 text-white' : 'background text-color-300 hover:text-secondary-600'
                )}>
                <BsTable size={14}/>
            </button>
            <button
                type="button"
                title={t('card')}
                onClick={() => setViewMode('card')}
                style={{width: 40, height: 40}}
                className={classNames(
                    "flex flex-center transition-colors border-l border-color-200",
                    viewMode === 'card' ? 'bg-secondary-500 text-white' : 'background text-color-300 hover:text-secondary-600'
                )}>
                <BsGrid3X3GapFill size={14}/>
            </button>
        </div>
    )

    const [pager_widget] = usePaginationHelper({
        renderList,
        renderFilter: NullAsDefault,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        // Toggle Bảng/Thẻ đặt giữa ô search và nút Lọc (renderHeader) - không đụng search bar hay
        // nút Lọc (ButtonAdvance do ListFilter tự render ngoài renderHeader).
        renderHeader: ([search_bar]) => (
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">{search_bar}</div>
                {viewModeToggle}
            </div>
        ),
        dependencies: [owner_id, owner_type],
        withKey: createPagerKey(refresh_deps),
        query_params: filter_actions,
        AdvancedFilter: NullAsDefault,
        isToggleDefault: false,
    })

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('curriculum')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <div className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('overall structure for each type — used to build courses and track progress')}
                    </div>
                </div>
                <Button
                    color="primary" variant="solid"
                    className="px-6 py-2 font-semibold rounded-lg"
                    onClick={() => handleAction(CurriculumActionTypes.CURRICULUM_CREATE)}>
                    {t('create curriculum')}
                </Button>
            </div>
            {pager_widget}
            {modal}
            <div className="h-16"/>
        </div>
    )
}

const Curricula = compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(CurriculaManager)

export default Curricula
